import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MFADevice {
  id: string;
  device_name: string;
  device_fingerprint: string;
  trusted_until: string;
  last_used_at: string;
}

export const useMFA = () => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<MFADevice[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    checkMFAStatus();
    loadDevices();
  }, []);

  const checkMFAStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: factors } = await supabase.auth.mfa.listFactors();
      setMfaEnabled(factors && factors.totp && factors.totp.length > 0);
    } catch (error) {
      console.error('Error checking MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const { data, error } = await supabase
        .from('mfa_devices')
        .select('*')
        .order('last_used_at', { ascending: false });

      if (error) throw error;
      setDevices(data || []);
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const enrollMFA = async () => {
    try {
      // First check and remove any existing factors
      const { data: factors } = await supabase.auth.mfa.listFactors();
      if (factors && factors.totp && factors.totp.length > 0) {
        for (const factor of factors.totp) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }

      // Now enroll the new factor
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App'
      });

      if (error) throw error;
      return data;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: error.message
      });
      return null;
    }
  };

  const verifyMFA = async (factorId: string, code: string) => {
    try {
      const { data, error } = await supabase.auth.mfa.challenge({ factorId });
      if (error) throw error;

      const challengeId = data.id;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code
      });

      if (verifyError) throw verifyError;

      await checkMFAStatus();
      
      // Send confirmation email
      await supabase.functions.invoke('send-mfa-enabled-email');

      toast({
        title: 'Erfolg',
        description: 'Zwei-Faktor-Authentifizierung wurde aktiviert'
      });

      return true;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Ungültiger Code'
      });
      return false;
    }
  };

  const unenrollMFA = async (factorId: string) => {
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;

      await checkMFAStatus();
      
      // Send notification email
      await supabase.functions.invoke('send-mfa-disabled-email');

      toast({
        title: 'Erfolg',
        description: 'Zwei-Faktor-Authentifizierung wurde deaktiviert'
      });

      return true;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: error.message
      });
      return false;
    }
  };

  const removeDevice = async (deviceId: string) => {
    try {
      const { error } = await supabase
        .from('mfa_devices')
        .delete()
        .eq('id', deviceId);

      if (error) throw error;
      
      await loadDevices();
      toast({
        title: 'Erfolg',
        description: 'Gerät wurde entfernt'
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: error.message
      });
    }
  };

  return {
    mfaEnabled,
    loading,
    devices,
    enrollMFA,
    verifyMFA,
    unenrollMFA,
    removeDevice,
    refresh: () => {
      checkMFAStatus();
      loadDevices();
    }
  };
};
